import { type ToolEntry, toolEntryName, toolEntryNames, toolEntryOptional } from '@zerohuman/shared'

/** A task's tools as the editor shows them: each entry's tool name, one per line, never an object printed as text. */
export function toolEditorText(tools: readonly ToolEntry[] | null | undefined): string {
    return toolEntryNames(tools ?? []).join('\n')
}

/** The tools a task marks optional, one per line, as the editor keeps them beside `toolEditorText`. */
export function optionalToolsText(tools: readonly ToolEntry[] | null | undefined): string {
    return toolEntryNames((tools ?? []).filter(toolEntryOptional)).join('\n')
}

/**
 * The editor's tool names as the entries a save sends (os#1687). A name the version already holds goes back as the
 * entry it is stored as, so an object entry's own keys (its `gates`, say) survive a save that did not touch that
 * tool; a name the editor added goes as written, and the API stores it in the one shape.
 *
 * With `optional` (the editor's optional tools, one per line), each entry is marked `optional: true` when its name is
 * listed there and loses the mark when it is not. Left out, every entry keeps the mark it was stored with.
 */
export function toolEntriesFromEditor(
    text: string,
    stored: readonly ToolEntry[] | null | undefined,
    optional?: string,
): ToolEntry[] {
    const byName = new Map<string, ToolEntry>()
    for (const entry of stored ?? []) {
        const name = toolEntryName(entry)
        if (name !== null && !byName.has(name)) {
            byName.set(name, entry)
        }
    }
    const marked = optional === undefined ? null : new Set(lines(optional))
    return lines(text).map(name => {
        const entry = byName.get(name) ?? name
        if (marked === null || marked.has(name) === toolEntryOptional(entry)) {
            return entry
        }
        const { optional: _was, ...rest } = typeof entry === 'string' ? { name: entry } : entry
        return marked.has(name) ? { ...rest, optional: true } : rest
    })
}

function lines(text: string): string[] {
    return text
        .split('\n')
        .map(line => line.trim())
        .filter(Boolean)
}
